"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { MapPin, Phone, Mail, Star, ShieldCheck, ChevronRight, Award, ArrowRight } from "lucide-react";

interface GarageImage {
  id: string;
  url: string;
  alt?: string | null;
}

interface Garage {
  id: string;
  name: string;
  slug: string | null;
  city: string;
  county: string;
  address?: string | null;
  postcode?: string | null;
  phone?: string | null;
  email?: string | null;
  services?: string;
  rating: number | null;
  reviewCount: number;
  image: GarageImage | null;
}

export const defaultGarages: Garage[] = [
  {
    id: "1",
    name: "Apex Motors London",
    slug: "apex-motors-london",
    city: "London",
    county: "Greater London",
    address: "142 High Street, Westminster",
    postcode: "SW1A 1AA",
    phone: "020 7946 0958",
    email: "info@apexmotors.uk",
    services: "MOT, Servicing, Repairs",
    rating: 4.9,
    reviewCount: 127,
    image: null,
  },
  {
    id: "2",
    name: "Premier Auto Centre",
    slug: "premier-auto-centre",
    city: "Manchester",
    county: "Greater Manchester",
    address: "88 Deansgate Boulevard",
    postcode: "M1 1AD",
    phone: "0161 234 5678",
    email: "info@premierauto.uk",
    services: "MOT, Diagnostics, Tyres",
    rating: 4.9,
    reviewCount: 89,
    image: null,
  },
  {
    id: "3",
    name: "Reliable Motors",
    slug: "reliable-motors",
    city: "Birmingham",
    county: "West Midlands",
    address: "24 New Street Service Bay",
    postcode: "B1 1AA",
    phone: "0121 234 5678",
    email: "info@reliablemotors.uk",
    services: "MOT, Service, Repairs",
    rating: 4.8,
    reviewCount: 156,
    image: null,
  },
  {
    id: "4",
    name: "QuickFix Auto Care",
    slug: "quickfix-auto-care",
    city: "Bristol",
    county: "Avon",
    address: "12 Temple Way Unit 4",
    postcode: "BS1 1AA",
    phone: "0117 234 5678",
    email: "info@quickfix.uk",
    services: "MOT, Servicing, Diagnostics",
    rating: 4.8,
    reviewCount: 94,
    image: null,
  },
];

interface FeaturedGaragesProps {
  initialGarages?: Garage[];
}

export default function FeaturedGarages({ initialGarages = defaultGarages }: FeaturedGaragesProps) {
  const router = useRouter();
  const [garages, setGarages] = useState<Garage[]>(initialGarages);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchGarages = async () => {
      try {
        const response = await fetch("/api/garages/featured", { cache: "no-store" });
        if (!response.ok) return;
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setGarages(data);
        }
      } catch (err) {
        console.error("Error fetching featured garages:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchGarages();
  }, []);

  const formatGarageName = (rawName: string) => {
    if (!rawName) return "Garage";
    const cleaned = rawName.trim();
    if (cleaned.toLowerCase() === "ali ahmd" || cleaned.toLowerCase() === "ali ahmad") {
      return "Ali Ahmad Motors";
    }
    if (cleaned.toLowerCase() === "acd") {
      return "ACD Auto Services";
    }
    if (cleaned.toLowerCase().startsWith("abc - branch")) {
      return "ABC Auto Centre (Branch 2)";
    }
    return cleaned;
  };

  const displayGarages = (garages.length > 0 ? garages : defaultGarages).map((g) => ({
    ...g,
    name: formatGarageName(g.name),
  }));

  const handleCardClick = (garage: Garage) => {
    router.push(`/garages/${garage.slug || garage.id}`);
  };

  return (
    <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-linear-to-b from-slate-50 via-white to-blue-50/30">
      <div className="max-w-7xl mx-auto">
        
        {/* Section Header */}
        <header className="text-center mb-14">
          <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold uppercase tracking-widest mb-3">
            VERIFIED UK NETWORK
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 mt-2">
            Used by Leading UK{" "}
            <span className="bg-linear-to-r from-blue-600 via-cyan-500 to-teal-500 bg-clip-text text-transparent">
              Garages
            </span>
          </h2>
          <p className="text-slate-600 mt-3 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Independent MOT testing stations, mobile mechanics, and automotive workshops scaling with GarageBoost.
          </p>
        </header>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent mb-3"></div>
            <p className="text-slate-600 font-bold text-sm">Loading featured workshops...</p>
          </div>
        ) : displayGarages.length === 0 ? (
          <div className="text-center py-12 text-slate-500 text-sm">No verified workshops listed yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {displayGarages.slice(0, 4).map((garage) => {
              const firstImage = garage.image;

              return (
                <div
                  key={garage.id}
                  onClick={() => handleCardClick(garage)}
                  className="group bg-white rounded-3xl overflow-hidden shadow-xs hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-slate-200/90 hover:border-blue-300 flex flex-col justify-between cursor-pointer"
                  role="link"
                  tabIndex={0}
                  aria-label={`View ${garage.name}`}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleCardClick(garage);
                    }
                  }}
                >
                  {/* Image Header Banner */}
                  <div className="relative h-44 bg-linear-to-br from-slate-900 via-slate-800 to-blue-950 overflow-hidden">
                    {firstImage ? (
                      <Image
                        src={firstImage.url}
                        alt={firstImage.alt || garage.name}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <Award size={40} className="text-blue-400/60 mx-auto mb-1.5" />
                          <span className="text-xs font-bold text-slate-300">Verified Workshop</span>
                        </div>
                      </div>
                    )}

                    {/* Top Verified Badge */}
                    <div className="absolute top-3.5 left-3.5 flex items-center gap-2">
                      <span className="bg-slate-950/80 backdrop-blur-md text-teal-300 text-[11px] font-bold px-2.5 py-1 rounded-full border border-teal-500/30 flex items-center gap-1">
                        <ShieldCheck size={13} className="text-teal-400" /> Verified
                      </span>
                    </div>

                    {/* Rating Badge */}
                    {garage.rating && (
                      <div className="absolute bottom-3.5 left-3.5 bg-amber-400 text-slate-950 px-2.5 py-0.5 rounded-full font-black text-xs shadow-md flex items-center gap-1">
                        <Star size={12} fill="currentColor" />
                        {garage.rating}
                      </div>
                    )}
                  </div>

                  {/* Card Content Body */}
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      {/* Name */}
                      <h3 className="text-base font-extrabold text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-1">
                        {garage.name}
                      </h3>

                      {/* Location Box */}
                      <div className="bg-slate-50 rounded-xl p-2.5 my-3 border border-slate-200/80 flex items-start gap-2">
                        <MapPin size={15} className="text-blue-600 shrink-0 mt-0.5" />
                        <div className="text-xs text-slate-700 leading-snug">
                          <span className="font-bold text-slate-900 block">
                            {garage.city || garage.county || "United Kingdom"}
                          </span>
                          <span className="text-slate-500 truncate block">
                            {garage.address || garage.postcode || "UK Registered Location"}
                          </span>
                        </div>
                      </div>

                      {/* Contact Info */}
                      <div className="space-y-1.5 mb-4">
                        {garage.phone && (
                          <a
                            href={`tel:${garage.phone}`}
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-2 text-xs text-slate-600 hover:text-blue-600 transition-colors"
                          >
                            <span className="w-5 h-5 rounded-md bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
                              <Phone size={11} />
                            </span>
                            <span className="font-semibold">{garage.phone}</span>
                          </a>
                        )}
                        {garage.email && (
                          <a
                            href={`mailto:${garage.email}`}
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-2 text-xs text-slate-600 hover:text-blue-600 transition-colors truncate"
                          >
                            <span className="w-5 h-5 rounded-md bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
                              <Mail size={11} />
                            </span>
                            <span className="font-medium truncate">{garage.email}</span>
                          </a>
                        )}
                      </div>
                    </div>

                    {/* View Details Button */}
                    <div className="pt-3 border-t border-slate-100">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardClick(garage);
                        }}
                        className="w-full bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white font-bold py-2.5 px-4 rounded-xl transition-all shadow-md shadow-blue-500/20 hover:shadow-blue-500/40 flex items-center justify-center gap-1.5 text-xs sm:text-sm group/btn cursor-pointer"
                      >
                        <span>View Garage Profile</span>
                        <ChevronRight size={15} className="group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Explore All Garages CTA Button */}
        <div className="text-center mt-12">
          <Link
            href="/find-garage"
            className="inline-flex items-center gap-2 bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white px-8 py-3.5 rounded-xl font-bold text-base shadow-xl shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.02] transition-all"
          >
            <span>Explore All UK Garages</span>
            <ArrowRight size={18} />
          </Link>
        </div>

      </div>
    </section>
  );
}
